// ApexCharts, dressed in this product's visual system rather than its own.
//
// Loaded on demand: the panel is a tool people keep open in a tab, and 824 kB of
// charting library on the moderation queue's critical path would be weight spent
// on a page most sessions never open. The dynamic import inside mountChart() is
// what splits it into its own chunk — its stylesheet travels with it there
// rather than as a static import here, or Vite folds 30 KB of chart CSS into
// the entry stylesheet every publisher downloads on every /panel page for a
// chart only moderators on one route ever see. See mountChart() below.

// Colours come from the computed tokens rather than from literals — the same
// rule public/basemap.js follows for the map. A renderer cannot read a
// stylesheet, so the only way "no colour outside tokens.css" survives it is to
// read the tokens and hand them over.
export function chartTheme() {
  const css = getComputedStyle(document.documentElement);
  const token = (name, fallback) => css.getPropertyValue(name).trim() || fallback;
  return {
    accent: token('--accent', '#7066d0'),
    muted: token('--text-muted', '#a7a7a7'),
    line: token('--line', '#2e2e33'),
    text: token('--text', '#e2e2e2'),
    // Same expression public/theme.js uses to find the current theme: the
    // dataset attribute is set only by the manual toggle or the boot script
    // reading localStorage, so a moderator who never touched either falls
    // through to the OS preference instead of defaulting to dark.
    isLight: (document.documentElement.dataset.theme ||
      (matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark')) === 'light',
  };
}

/**
 * The house style, and every line of it is a DESIGN.md rule rather than taste:
 *
 * - **No radius, anywhere.** §2 deletes the radius token outright, so bars are
 *   square and so are markers. RadioDock's `borderRadius: 2` would be a rule
 *   break here, not a borrowed detail.
 * - **Markers are squares.** §2.3: "6 px squares, never circles."
 * - **Straight strokes.** A smooth curve is the same softening the radius rule
 *   removes everywhere else.
 * - **No shadow.** Another deleted token.
 * - **The grid is a hairline in `--line`**, the divider colour used between rows
 *   and fields — a chart's gridlines are dividers and should look like the ones
 *   above and below it.
 * - **The accent carries the data.** §2.3 forbids the accent *behind text* and
 *   certifies it at 3:1 for a 6 px square — a data mark is neither, so the
 *   leading series is `--accent` and a second one is `--text-muted`. Setting it
 *   here rather than at each call site is what keeps ApexCharts' own `#008FFB`
 *   out of the product: left alone the library paints a raw blue, which is a
 *   colour outside tokens.css and therefore a bug.
 */
// The bucket readStats() actually drew the series at (server/lib/analytics.js).
// An axis left to infer its own granularity from point spacing labels a
// 365-day, month-bucketed chart in days — this is what makes the number the
// server already computed and returns as `series.unit` worth reading at all.
function xAxisFormat(unit) {
  if (unit === 'hour') return 'HH:mm';
  if (unit === 'month') return 'MMM yyyy';
  return 'dd MMM';
}

export function baseOptions(theme, { unit } = {}) {
  return {
    colors: [theme.accent, theme.muted],
    chart: {
      height: 240,
      background: 'transparent',
      fontFamily: 'inherit',
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
      dropShadow: { enabled: false },
      parentHeightOffset: 0,
    },
    grid: {
      borderColor: theme.line,
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      padding: { left: 0, right: 0 },
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'straight', width: 2 },
    // Solid, never a gradient.
    fill: { type: 'solid', opacity: 0.12 },
    markers: { size: 0, shape: 'square', strokeWidth: 0 },
    plotOptions: { bar: { borderRadius: 0, columnWidth: '62%' } },
    legend: { show: false },
    tooltip: { theme: theme.isLight ? 'light' : 'dark' },
    xaxis: {
      type: 'datetime',
      labels: {
        style: { colors: theme.muted, fontSize: '11px' },
        ...(unit ? { format: xAxisFormat(unit) } : {}),
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { stroke: { color: theme.line, dashArray: 0 } },
    },
    yaxis: { labels: { style: { colors: theme.muted, fontSize: '11px' } } },
  };
}

// Returns null rather than throwing. A chart that cannot mount must leave the
// numbers above it standing — the tiles are the part that matters, and a missing
// chart is not a reason for a moderation dashboard to show an error.
//
// **The node has to be in the document before this is called.** ApexCharts
// measures its container in render(), a detached node reads as zero-size, and
// nothing resizes it afterwards. That is the caller's guarantee to make and not
// this function's to wait for: a wait here would be a listener on the document
// with no owner, no timeout and nothing to cancel it when the view it belonged
// to is torn down mid-flight — which is a worse leak than the one it would be
// protecting against. See how stats.js defers its mounts past the router's
// append instead.
//
// **The stylesheet is imported here, alongside the library, not injected.**
// ApexCharts ships base CSS and will inject it as a `<style>` element if it is
// absent — which the production policy refuses, and the chart then overflows
// its own canvas. Measured on 2026-08-09: with the file linked the layout is
// correct; the library still injects two `<style>` elements and both are still
// refused, harmlessly, because the linked file already carries what they hold.
// Vite emits a dynamic import's CSS as its own `<link>` in the chunk that
// requested it, so this needs no CSP change — the file is still `self`-origin,
// just no longer in the bundle every page pays for.
export async function mountChart(node, options) {
  try {
    const [{ default: ApexCharts }] = await Promise.all([
      import('apexcharts'),
      import('apexcharts/dist/apexcharts.css'),
    ]);
    const chart = new ApexCharts(node, options);
    await chart.render();
    return chart;
  } catch {
    return null;
  }
}
